import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../api';
import EditJobModal from './EditJobModal';
import { ALL_DEPARTMENTS } from './jobPricing';
import { getISOWeek, getFYWeek, shiftWeek, thisWeekStart, weekLabel, weekRangeLabel, getWeekRange } from '../weekUtils';
import ExcelFilter from './ExcelFilter';
import { fmtHM } from './ReportsPanel';
import { useCustomer } from '../contexts/CustomerContext';
import {
  CalendarBlank, MagnifyingGlass, Island,
  CurrencyDollar, UsersThree, TrendUp, Percent, Coin, Receipt, UserCircle,
  Car, Wrench, Buildings, House, Briefcase, Sparkle, User, CaretLeft, CaretRight,
  Cake,
} from '@phosphor-icons/react';

// ── Department → icon + color ────────────────────────────────────────────
const DEPT_ICONS = {
  'KIA CARS':     { Icon: Car,      color: '#3B82F6', bg: '#DBEAFE' },
  'SUZUKI CARS':  { Icon: Car,      color: '#EF4444', bg: '#FEE2E2' },
  'GWM CARS':     { Icon: Car,      color: '#22C55E', bg: '#DCFCE7' },
  'GAC CARS':     { Icon: Car,      color: '#EA580C', bg: '#FFEDD5' },
  'NEW CARS':     { Icon: Car,      color: '#F59E0B', bg: '#FEF3C7' },
  'USED CARS':    { Icon: Car,      color: '#A855F7', bg: '#F3E8FF' },
  'SERVICE':      { Icon: Wrench,   color: '#0EA5E9', bg: '#E0F2FE' },
  'OFFICE':       { Icon: Buildings,color: '#6366F1', bg: '#E0E7FF' },
  'PRIVATE':      { Icon: House,    color: '#EC4899', bg: '#FCE7F3' },
  'PRIVATE CAR':  { Icon: House,    color: '#EC4899', bg: '#FCE7F3' },
  'Unassigned':   { Icon: Briefcase,color: '#94A3B8', bg: '#F1F5F9' },
};

// ── Department constants ──────────────────────────────────────────────────

const DEPT_COLORS = {
  'KIA CARS':    { bg: '#e0f0ff', color: '#1d6fa4' },
  'SUZUKI CARS': { bg: '#ffe0e0', color: '#b91c1c' },
  'GWM CARS':    { bg: '#fff0d0', color: '#b45309' },
  'GAC CARS':    { bg: '#ffede0', color: '#c2410c' },
  'NEW CARS':    { bg: '#fff7e0', color: '#92400e' },
  'USED CARS':   { bg: '#e0ffe8', color: '#15803d' },
  'SERVICE':     { bg: '#f0e8ff', color: '#7c3aed' },
  'OFFICE':      { bg: '#f0f4ff', color: '#3730a3' },
  'PRIVATE':     { bg: '#fce7f3', color: '#be185d' },
  'PRIVATE CAR': { bg: '#fce7f3', color: '#be185d' }, // legacy alias
  'Unassigned':  { bg: '#f3f4f6', color: '#9ca3af' },
};

// Dealership departments show their brand logo in the chip — the logo says
// "CARS" so the word is dropped. Non-brand departments keep a text chip.
const DEPT_LOGO = {
  'KIA CARS': 'kia.png', 'SUZUKI CARS': 'suzuki.png',
  'GWM CARS': 'gwm.png', 'GAC CARS': 'gac.png',
};
function DeptBadge({ dept }) {
  if (!dept) return <span style={{ color: '#d1d5db', fontSize: 11 }}>—</span>;
  const logo = DEPT_LOGO[dept];
  if (logo) {
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        background: '#f4f6f8', color: '#1a1a2e', fontWeight: 700, fontSize: 11,
        padding: '1px 8px 1px 5px', borderRadius: 6, whiteSpace: 'nowrap',
      }}>
        <img src={`${process.env.PUBLIC_URL}/brand-logos/${logo}`} alt=""
          style={{ width: 26, height: 22, objectFit: 'contain', flex: 'none' }} />
        {dept.replace(/\s*CARS$/, '')}
      </span>
    );
  }
  const c = DEPT_COLORS[dept] || DEPT_COLORS['Unassigned'];
  return (
    <span style={{
      background: c.bg, color: c.color, fontWeight: 700,
      padding: '2px 8px', borderRadius: 6, fontSize: 11, whiteSpace: 'nowrap',
    }}>
      {dept}
    </span>
  );
}

// ── helpers ───────────────────────────────────────────────────────────────
const NUMS = { fontVariantNumeric: 'tabular-nums' };

// Rows that need the boss's eye get a tint + left bar. Approved/completed
// rows stay plain — the Status chip already says they're settled.
const ATTENTION_ROW = {
  pending:  { bg: '#fffbeb', bar: '#f59e0b' },
  rejected: { bg: '#fff5f5', bar: '#e01818' },
  new:      { bg: '#f4f6f8', bar: '#4A7A91' },
};

// Status chips — every status is a chip with the same anatomy.
const STATUS_CHIP = {
  approved:  { bg: '#dcfce7', color: '#15803d' },
  completed: { bg: '#dcfce7', color: '#15803d' },
  pending:   { bg: '#fef3c7', color: '#a16207' },
  rejected:  { bg: '#fee2e2', color: '#b91c1c' },
  new:       { bg: '#e8f2f7', color: '#1e3a4a' },
};
function StatusChip({ status }) {
  const s = STATUS_CHIP[status] || { bg: '#f1f3f5', color: '#5a6a74' };
  return (
    <span style={{
      background: s.bg, color: s.color, fontSize: 9.5, fontWeight: 700,
      textTransform: 'uppercase', padding: '3px 7px', borderRadius: 6, whiteSpace: 'nowrap',
    }}>{status}</span>
  );
}

// Model names are Title Case; ALL CAPS is reserved for services and
// departments. Genuine alphanumeric codes (EV5, K4) are left alone.
function titleCaseModel(m) {
  const s = String(m || '').trim();
  if (!s) return s;
  return s.split(/\s+/).map(w =>
    /\d/.test(w) ? w : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
  ).join(' ');
}

// Row-menu item.
const menuItem = (color) => ({
  display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px',
  border: 'none', background: 'none', borderRadius: 7,
  fontSize: 12.5, fontWeight: 700, color, cursor: 'pointer',
});

// Day group heading, e.g. "THU 31 JUL".
const DAY_ABBR = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MON_ABBR = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
function dayHeading(dateStr) {
  const [y, m, d] = String(dateStr || '').slice(0, 10).split('-').map(Number);
  if (!y) return '—';
  const dt = new Date(y, m - 1, d);
  return `${DAY_ABBR[dt.getDay()]} ${d} ${MON_ABBR[m - 1]}`;
}

// KPI strip pieces.
const KpiDiv = () => <span style={{ width: 1, height: 34, background: '#dfe3e7', margin: '0 18px' }} />;
function Kpi({ label, value, sub, color, badge, big }) {
  return (
    <div>
      <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 1.3, textTransform: 'uppercase', color: '#8a949c' }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontSize: big ? 25 : 17, fontWeight: 800, letterSpacing: big ? -0.8 : 0, color: color || '#0a0a0a', ...NUMS }}>{value}</span>
        {badge && (
          <span style={{ fontSize: 12, fontWeight: 800, background: '#eafaf0', color: '#15803d', borderRadius: 6, padding: '2px 7px', ...NUMS }}>{badge}</span>
        )}
      </div>
      {sub && <div style={{ fontSize: 11, fontWeight: 600, color: '#8a949c', ...NUMS }}>{sub}</div>}
    </div>
  );
}

// A comment only earns screen space when it says something the service line
// doesn't already ("DEMO"/"DEMO" was noise). Returns the note in Title case,
// or '' when it just echoes the service.
function realNote(comment, service) {
  const parts = String(comment || '').split(/\s*[,·]\s*/).map(s => s.trim()).filter(Boolean);
  const svc = String(service || '').trim().toUpperCase();
  const kept = parts.filter(p => {
    const u = p.toUpperCase();
    return u !== svc && u !== 'N/A' && !svc.includes(u);
  });
  return kept.map(p => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join(' · ');
}

function formatTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDateDMY(dateStr) {
  if (!dateStr) return '—';
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
}

function firstName(fullName) {
  if (!fullName) return '—';
  // Separate shared jobs with '·' — a comma reads like two separate jobs.
  return fullName.split(',').map(n => n.trim().split(' ')[0]).join(' · ');
}

const DAILY_SERVICE_CODES = {
  'SERVICE WASH DAILY': 'SWD',
  'DAILY OFFICE CLEAN': 'DOC',
  'MOP SHOWROOM': 'MOP',
  'HOURS': 'HRS',
  'RECLEAN': 'RCL',
};

function stockDisplay(job) {
  if (job.stock_number) return job.stock_number;
  const code = DAILY_SERVICE_CODES[job.job_description];
  if (code) {
    const d = job.date ? job.date.replace(/-/g, '').slice(4) : '';
    return `${code}-${d}`;
  }
  return '—';
}

const thStyle = { padding: '5px 5px', fontSize: 11, fontWeight: 700, textAlign: 'left', whiteSpace: 'nowrap', borderBottom: '2px solid #e5e7eb' };

// ── Main component ────────────────────────────────────────────────────────
export default function AdminDashboard({ user, view, slotAfterPayroll }) {
  const today = new Date().toISOString().slice(0, 10);

  const [selectedIds, setSelectedIds] = useState(new Set());   // bulk-action selection
  const [bulkBusy, setBulkBusy] = useState(false);
  const [openMenuId, setOpenMenuId] = useState(null);          // row action menu
  // Active workspace (multi-customer). customerParam is the customer_id to
  // send, or undefined in "View All" mode. Switching workspace re-runs
  // loadData via the dependency array below.
  const { customerParam } = useCustomer();

  const currentYear = new Date().getFullYear();
  const [jobs, setJobs]                   = useState([]);
  const [summary, setSummary]             = useState(null);
  const [contractors, setContractors]     = useState([]);
  // ── View mode: 'week' (default, fast) or 'year' (research) ──────────────
  const [viewMode, setViewMode]           = useState('week');
  const [weekStart, setWeekStart]         = useState(thisWeekStart); // Monday YYYY-MM-DD
  const [weeklyExpenses,     setWeeklyExpenses]     = useState(0);
  const [weeklyExpBreakdown, setWeeklyExpBreakdown] = useState({});
  const [filterYears, setFilterYears]     = useState(new Set([String(currentYear - 1), String(currentYear)]));
  // Excel-style multi-select filters (client-side, empty Set = all)
  const [filterStaff, setFilterStaff]     = useState(new Set());
  const [filterStatus, setFilterStatus]   = useState(new Set());
  const [filterDept, setFilterDept]       = useState(new Set());
  const [filterService, setFilterService] = useState(new Set());
  const [needsAttention, setNeedsAttention] = useState(false);   // show only non-approved jobs
  const [filterDate, setFilterDate]       = useState('');  // single day quick filter
  const [filterStock, setFilterStock]     = useState('');  // stock number search
  // Debounced server-side copy — used to extend the search past the
  // backend's 2000-row cap in All mode without refetching on every keystroke.
  const [debouncedStock, setDebouncedStock] = useState('');
  const [filterModel, setFilterModel]     = useState(new Set()); // car model
  const [loading, setLoading]             = useState(true);
  const [firstOccurrenceIds, setFirstOccurrenceIds] = useState(new Set());
  const [editingJob, setEditingJob]       = useState(null);
  // Office-clean rows aren't jobs — they're the cleaner's clock record, shown
  // in this list so the day reads whole. The job editor can't save one (the id
  // is "oc-1700-0", not an integer), so they get the one edit that was
  // genuinely missing: moving the day to whoever actually worked it.
  const [reassigning, setReassigning]     = useState(null);
  const [editingCell, setEditingCell]     = useState(null); // { key, value } — key = "lineId-field" or "jobId-field"
  const [savingCell, setSavingCell]       = useState(null); // key being saved
  const [todayHoliday, setTodayHoliday]  = useState(null);

  // ── Auto-fetch weekly expenses from bank statement transactions ───────────
  useEffect(() => {
    if (viewMode !== 'week') return;
    const { week, year } = getISOWeek(weekStart);
    api.getWeeklyExpenses(week, year)
      .then(data => {
        setWeeklyExpenses(data.total || 0);
        setWeeklyExpBreakdown(data.breakdown || {});
      })
      .catch(() => { setWeeklyExpenses(0); setWeeklyExpBreakdown({}); });
  }, [weekStart, viewMode]);

  // ── Hourly days override: { [userId_weekKey]: days } ─────────────────────
  const [hourlyDaysOverride, setHourlyDaysOverride] = useState({});
  useEffect(() => {
    const { week, year } = getISOWeek(weekStart);
    const wk = `${year}-W${String(week).padStart(2,'0')}`;
    const stored = localStorage.getItem(`hourlyDays_${wk}`);
    setHourlyDaysOverride(stored ? JSON.parse(stored) : {});
  }, [weekStart]);

  const setHourlyDays = (userId, days) => {
    const { week, year } = getISOWeek(weekStart);
    const wk = `${year}-W${String(week).padStart(2,'0')}`;
    const next = { ...hourlyDaysOverride, [userId]: days };
    setHourlyDaysOverride(next);
    localStorage.setItem(`hourlyDays_${wk}`, JSON.stringify(next));
  };

  // In All mode we deliberately defer the DB hit until the admin actually
  // narrows the search. Otherwise every visit to /jobs would either return
  // the full table or get capped at 2000 rows — both unhelpful at scale.
  // Considered "narrowing" filters (any of these triggers a fetch in All mode):
  const hasFilter = (
    debouncedStock.trim().length > 0
    || filterStatus.size > 0
    || filterDept.size > 0
    || filterDate
  );

  const loadData = useCallback(async () => {
    // Build backend params based on view mode
    let jobParams = {};
    let summaryParams = {};

    if (filterDate) {
      jobParams     = { date: filterDate };
      summaryParams = { date: filterDate };
    } else if (viewMode === 'week') {
      const { week: wn, year: wy } = getISOWeek(weekStart);
      jobParams     = { week_number: wn, week_year: wy };
      summaryParams = { week_number: wn, week_year: wy };
    } else if (viewMode === 'year') {
      const yrs = [...filterYears].join(',');
      jobParams     = filterYears.size > 0 ? { year: yrs } : {};
      summaryParams = filterYears.size > 0 ? { year: yrs } : {};
    } else {
      // 'all' mode — only fetch when there's an active filter. Otherwise the
      // request would either pull the whole DB or hit the 2000-row cap with
      // nothing useful for the admin. Show empty state + hint instead.
      if (!hasFilter) {
        setJobs([]);
        setSummary(null);
        setLoading(false);
        return;
      }
      jobParams     = {};
      summaryParams = {};
    }

    // Stock filter goes to the DB so it reaches every year, not just the
    // most-recent slice the row cap would otherwise return.
    if (debouncedStock.trim()) {
      jobParams.stock_number     = debouncedStock.trim();
      summaryParams.stock_number = debouncedStock.trim();
    }
    // Status / dept filters: backend supports a single value, so only pass
    // when exactly one is selected. Multi-select still falls through to the
    // client-side filter on whatever the narrower fetch returned.
    if (filterStatus.size === 1) {
      const v = [...filterStatus][0];
      jobParams.status     = v;
      summaryParams.status = v;
    }
    if (filterDept.size === 1) {
      const v = [...filterDept][0];
      jobParams.department     = v;
      summaryParams.department = v;
    }

    // Multi-customer: scope jobs + summary to the active workspace. Omitted
    // in View All mode (customerParam undefined) so the consolidated view
    // shows every customer.
    if (customerParam) {
      jobParams.customer_id     = customerParam;
      summaryParams.customer_id = customerParam;
    }

    setLoading(true);
    try {
      // Use allSettled so one failure doesn't block jobs from loading.
      // first-occurrences piggybacks on every refresh — the result is the
      // set of "first job per stock_number" job IDs, used to render the
      // green NEW badge. Without it here, freshly-logged jobs (a stock
      // seen for the first time) wouldn't get a NEW badge until the page
      // is hard-refreshed.
      const [jobsRes, summaryRes, ctrsRes, firstOccRes] = await Promise.allSettled([
        api.getJobs(jobParams),
        api.getSummary(summaryParams),
        api.getContractors(),
        api.getFirstOccurrences(),
      ]);

      if (jobsRes.status === 'fulfilled')    setJobs(jobsRes.value);
      else console.error('Jobs load failed:', jobsRes.reason);

      if (summaryRes.status === 'fulfilled') setSummary(summaryRes.value);
      else console.error('Summary load failed:', summaryRes.reason);

      if (ctrsRes.status === 'fulfilled')    setContractors(ctrsRes.value);
      else console.error('Contractors load failed:', ctrsRes.reason);

      if (firstOccRes.status === 'fulfilled') setFirstOccurrenceIds(new Set(firstOccRes.value));
      else console.error('getFirstOccurrences error:', firstOccRes.reason);

    } catch (err) { console.error('loadData error:', err); }
    finally { setLoading(false); }
  }, [viewMode, weekStart, filterYears, filterDate, debouncedStock, filterStatus, filterDept, hasFilter, customerParam]);

  useEffect(() => { loadData(); }, [loadData]);

  // Debounce filterStock so we don't refetch on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedStock(filterStock), 350);
    return () => clearTimeout(t);
  }, [filterStock]);

  // first-occurrence IDs are now refetched as part of loadData (above) so
  // newly logged jobs get their NEW badge without the boss having to hard-
  // refresh. The previous one-time useEffect lived here.

  // Check if today is a public holiday
  useEffect(() => {
    api.checkPublicHoliday(today)
      .then(r => { if (r.is_holiday) setTodayHoliday(r.holiday); })
      .catch(() => {});
  }, [today]);

  // Birthday alerts — match contractors whose birthday lands today or
  // tomorrow (relative to `today`). The contractor record's `birthday` is a
  // YYYY-MM-DD string; we ignore the year and compare month/day so the alert
  // fires every year. Only active, non-admin contractors qualify.
  const birthdays = useMemo(() => {
    if (!Array.isArray(contractors) || contractors.length === 0) return [];
    const [, tm, td] = (today || '').split('-').map(Number);
    if (!tm || !td) return [];
    // Tomorrow's month/day, accounting for end-of-month rollover
    const t = new Date(`${today}T00:00:00`);
    t.setDate(t.getDate() + 1);
    const tmrMm = t.getMonth() + 1;
    const tmrDd = t.getDate();
    const matches = [];
    for (const c of contractors) {
      if (!c.birthday) continue;
      if (c.is_active === 0) continue;
      if (c.type === 'admin') continue;
      const m = c.birthday.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!m) continue;
      const bm = parseInt(m[2], 10);
      const bd = parseInt(m[3], 10);
      if (bm === tm && bd === td) matches.push({ ...c, when: 'today' });
      else if (bm === tmrMm && bd === tmrDd) matches.push({ ...c, when: 'tomorrow' });
    }
    // Tomorrow first (the boss asked for one-day-ahead notice), then today
    return matches.sort((a, b) => {
      if (a.when !== b.when) return a.when === 'tomorrow' ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }, [contractors, today]);

  async function handleDelete(id) {
    if (!window.confirm('Delete this job?')) return;
    await api.deleteJob(id); loadData();
  }
  async function handleApprove(id) { await api.updateJob(id, { status: 'approved' }); loadData(); }

  // ── Inline cell editing ────────────────────────────────────────────────────
  function startEdit(key, value) {
    setEditingCell({ key, value: value ?? '' });
  }

  async function commitBillingLine(lineId, field, value) {
    const key = `bl-${lineId}-${field}`;
    if (savingCell === key) return;
    setSavingCell(key);
    setEditingCell(null);
    try {
      await api.updateBillingLine(lineId, { [field]: value });
      loadData();
    } catch (err) { alert(err.message); }
    finally { setSavingCell(null); }
  }

  async function commitJobField(jobId, field, value) {
    const key = `job-${jobId}-${field}`;
    if (savingCell === key) return;
    setSavingCell(key);
    setEditingCell(null);
    try {
      await api.updateJob(jobId, { [field]: value });
      loadData();
    } catch (err) { alert(err.message); }
    finally { setSavingCell(null); }
  }

  function handleCellKeyDown(e, saveCallback, cancelCallback) {
    if (e.key === 'Enter')  { e.preventDefault(); saveCallback(); }
    if (e.key === 'Escape') { e.preventDefault(); cancelCallback(); }
  }

  // Parse billing lines from dept_lines string (format: service|||dept|||rev_full|||rev_pay)
  function parseBillingLines(job) {
    if (!job.dept_lines) {
      return [{ lineId: null, service: job.job_description || '', department: job.department || 'Unassigned', revenue_full: job.revenue_full || 0, revenue_pay: job.revenue_pay || 0 }];
    }
    return job.dept_lines.split(';;;').map(entry => {
      const p = entry.split('|||');
      return {
        lineId:       parseInt(p[0]) || null,
        service:      (p[1] || '').trim(),
        department:   (p[2] || '').trim() || 'Unassigned',
        revenue_full: parseFloat(p[3]) || 0,
        revenue_pay:  parseFloat(p[4]) || 0,
      };
    });
  }

  // Get individual payout for a specific employee in a job
  function getEmployeePayout(job, empId) {
    if (!empId) return job.revenue_pay || 0;
    // job.employee_ids comes from STRING_AGG as a comma-separated string;
    // normalise to numbers so indexOf lines up with the payouts array.
    const idsRaw = job.employee_ids || [];
    const ids = Array.isArray(idsRaw)
      ? idsRaw.map(Number)
      : String(idsRaw).split(',').map(s => parseInt(s, 10)).filter(n => !isNaN(n));
    const payouts = job.payouts ? String(job.payouts).split(',').map(Number) : [];
    const idx = ids.indexOf(Number(empId));
    return idx >= 0 && idx < payouts.length ? payouts[idx] : 0;
  }

  // Year options (fixed range)
  const yearOptions = useMemo(() =>
    [2022,2023,2024,2025,2026].map(y => ({ value: String(y), label: String(y) }))
  , []);

  // ── Cascading filter options (Excel-style — each derives from all others) ──
  const activeYears = useMemo(() => [...filterYears], [filterYears]);

  // All jobs is the base; backend already handles week/year/date scoping
  const baseForStaff = useMemo(() => {
    let r = jobs;
    if (filterDate) r = r.filter(j => j.date === filterDate);
    if (filterStatus.size > 0)  r = r.filter(j => filterStatus.has(j.status));
    if (filterDept.size > 0)    r = r.filter(j => filterDept.has(j.department));
    if (filterService.size > 0) r = r.filter(j => {
      const svcs = [j.job_description];
      if (j.dept_lines) j.dept_lines.split(';;;').forEach(e => { const s = (e.split('|||')[1]||'').trim(); if (s) svcs.push(s); });
      return svcs.some(s => filterService.has(s));
    });
    return r;
  }, [jobs, filterDate, filterStatus, filterDept, filterService]);

  const staffOptions = useMemo(() => {
    const seen = new Map();
    baseForStaff.forEach(j => {
      if (!j.assigned_employees) return;
      j.assigned_employees.split(', ').forEach(name => {
        const first = name.trim().split(' ')[0];
        if (!seen.has(first)) seen.set(first, name.trim());
      });
    });
    return [...seen.entries()].sort((a,b) => a[0].localeCompare(b[0]))
      .map(([first, full]) => ({ value: full, label: first }));
  }, [baseForStaff]);

  const baseForStatus = useMemo(() => {
    let r = jobs;
    if (filterDate) r = r.filter(j => j.date === filterDate);
    if (filterStaff.size > 0)   r = r.filter(j => j.assigned_employees && [...filterStaff].some(n => j.assigned_employees.includes(n)));
    if (filterDept.size > 0)    r = r.filter(j => filterDept.has(j.department));
    if (filterService.size > 0) r = r.filter(j => {
      const svcs = [j.job_description];
      if (j.dept_lines) j.dept_lines.split(';;;').forEach(e => { const s = (e.split('|||')[1]||'').trim(); if (s) svcs.push(s); });
      return svcs.some(s => filterService.has(s));
    });
    return r;
  }, [jobs, filterDate, filterStaff, filterDept, filterService]);

  const statusOptions = useMemo(() => {
    const vals = [...new Set(baseForStatus.map(j => j.status))].filter(Boolean).sort();
    return vals.map(v => ({ value: v, label: v.charAt(0).toUpperCase() + v.slice(1) }));
  }, [baseForStatus]);

  const baseForDept = useMemo(() => {
    let r = jobs;
    if (filterDate) r = r.filter(j => j.date === filterDate);
    if (filterStaff.size > 0)   r = r.filter(j => j.assigned_employees && [...filterStaff].some(n => j.assigned_employees.includes(n)));
    if (filterStatus.size > 0)  r = r.filter(j => filterStatus.has(j.status));
    if (filterService.size > 0) r = r.filter(j => {
      const svcs = [j.job_description];
      if (j.dept_lines) j.dept_lines.split(';;;').forEach(e => { const s = (e.split('|||')[1]||'').trim(); if (s) svcs.push(s); });
      return svcs.some(s => filterService.has(s));
    });
    return r;
  }, [jobs, filterDate, filterStaff, filterStatus, filterService]);

  const deptOptions = useMemo(() => {
    const vals = [...new Set(baseForDept.map(j => j.department))].filter(Boolean).sort();
    return vals.map(v => ({ value: v, label: v }));
  }, [baseForDept]);

  const baseForService = useMemo(() => {
    let r = jobs;
    if (filterDate) r = r.filter(j => j.date === filterDate);
    if (filterStaff.size > 0)   r = r.filter(j => j.assigned_employees && [...filterStaff].some(n => j.assigned_employees.includes(n)));
    if (filterStatus.size > 0)  r = r.filter(j => filterStatus.has(j.status));
    if (filterDept.size > 0)    r = r.filter(j => filterDept.has(j.department));
    return r;
  }, [jobs, filterDate, filterStaff, filterStatus, filterDept]);

  const serviceOptions = useMemo(() => {
    const vals = new Set();
    baseForService.forEach(j => {
      if (j.job_description) vals.add(j.job_description);
      if (j.dept_lines) j.dept_lines.split(';;;').forEach(e => { const s = (e.split('|||')[1]||'').trim(); if (s) vals.add(s); });
    });
    return [...vals].filter(Boolean).sort().map(v => ({ value: v, label: v }));
  }, [baseForService]);

  const modelOptions = useMemo(() => {
    const vals = [...new Set(jobs.map(j => j.model))].filter(Boolean).sort();
    return vals.map(v => ({ value: v, label: v }));
  }, [jobs]);

  // ── Apply all client-side filters to get final visible jobs ──────────────
  const baseJobs = useMemo(() => {
    let result = jobs;
    if (filterDate)             result = result.filter(j => j.date === filterDate);
    if (filterStock.trim())     result = result.filter(j => j.stock_number && j.stock_number.toLowerCase().includes(filterStock.trim().toLowerCase()));
    if (filterModel.size > 0)   result = result.filter(j => filterModel.has(j.model));
    if (filterStaff.size > 0)   result = result.filter(j => j.assigned_employees && [...filterStaff].some(name => j.assigned_employees.includes(name)));
    if (filterStatus.size > 0)  result = result.filter(j => filterStatus.has(j.status));
    // Department filter: a job has matching dept if ANY of its billing lines
    // is in that dept. Filtering by j.department alone (the top-level field)
    // catches jobs where the model auto-tags the dept (e.g. a Swift gets
    // tagged SUZUKI CARS even when the only line is FIRST CLEAN / SERVICE),
    // which then leaks SERVICE rows into a SUZUKI CARS view.
    if (filterDept.size > 0) {
      result = result.filter(j => {
        const lines = parseBillingLines(j);
        return lines.some(bl => filterDept.has(bl.department));
      });
    }
    if (filterService.size > 0) result = result.filter(j => filterService.has(j.job_description));
    return result;
  }, [jobs, filterDate, filterStock, filterModel, filterStaff, filterStatus, filterDept, filterService]);

  // "Needs attention" = anything not approved. Counted before the toggle is
  // applied, otherwise the badge would zero itself out once switched on.
  const attentionCount = useMemo(() => baseJobs.filter(j => j.status !== 'approved').length, [baseJobs]);
  const filteredJobs = useMemo(
    () => (needsAttention ? baseJobs.filter(j => j.status !== 'approved') : baseJobs),
    [baseJobs, needsAttention]);
  const jobUnits = useMemo(
    () => filteredJobs.reduce((s, j) => s + (parseFloat(j.units) || 1), 0), [filteredJobs]);

  // Per-day subtotals for the day group headers. Uses the same visible-line
  // rule as the footer so the day bars add up to the period total.
  const dayTotals = useMemo(() => {
    const m = {};
    filteredJobs.forEach(j => {
      const d = j.date || '';
      let lines = parseBillingLines(j);
      if (filterService.size > 0) lines = lines.filter(bl => filterService.has(bl.service));
      if (filterDept.size > 0)    lines = lines.filter(bl => filterDept.has(bl.department));
      if (!lines.length) return;
      const e = m[d] || (m[d] = { jobs: 0, units: 0, charged: 0, payout: 0 });
      e.jobs += 1;
      e.units += parseFloat(j.units) || 1;
      e.charged += lines.reduce((s, bl) => s + bl.revenue_full, 0);
      e.payout  += lines.reduce((s, bl) => s + bl.revenue_pay, 0);
    });
    return m;
  }, [filteredJobs, filterService, filterDept]);

  // ── Row selection + bulk actions ─────────────────────────────────────────
  const toggleSelect = (id) => setSelectedIds(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const allVisibleSelected = filteredJobs.length > 0 && filteredJobs.every(j => selectedIds.has(j.id));
  const toggleSelectAll = () => setSelectedIds(
    allVisibleSelected ? new Set() : new Set(filteredJobs.map(j => j.id)));

  async function bulkApprove() {
    const ids = [...selectedIds];
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      for (const id of ids) await api.updateJob(id, { status: 'approved' });
      setSelectedIds(new Set());
      loadData();
    } finally { setBulkBusy(false); }
  }
  async function bulkDelete() {
    const ids = [...selectedIds];
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} job${ids.length === 1 ? '' : 's'}? This cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      for (const id of ids) await api.deleteJob(id);
      setSelectedIds(new Set());
      loadData();
    } finally { setBulkBusy(false); }
  }

  // Single staff selected for payout column
  const singleStaff = filterStaff.size === 1 ? [...filterStaff][0] : null;
  const singleStaffId = singleStaff
    ? (contractors.find(c => c.name === singleStaff || c.name.startsWith(singleStaff + ' ')) || {}).id
    : null;

  const hasActiveFilter = filterDate || filterStock.trim() || filterModel.size > 0 || filterStaff.size > 0 || filterStatus.size > 0 || filterDept.size > 0 || filterService.size > 0;

  function clearAllFilters() {
    setFilterStock(''); setFilterModel(new Set());
    setFilterStaff(new Set()); setFilterStatus(new Set());
    setFilterDept(new Set()); setFilterService(new Set());
    setFilterDate('');
  }

  // ── Dynamic summary (pivot-table style — updates with ALL active filters) ─
  const dynamicSummary = useMemo(() => {
    const total_jobs = filteredJobs.length;
    const deptMap    = {};
    let total_revenue = 0;
    let total_payout  = 0;

    filteredJobs.forEach(j => {
      // Payout is always job-level
      total_payout += singleStaffId
        ? getEmployeePayout(j, singleStaffId)
        : (j.revenue_pay || 0);

      if (j.dept_lines) {
        // format: lineId|||service|||dept|||revenue_full|||revenue_pay
        j.dept_lines.split(';;;').forEach(entry => {
          const parts = entry.split('|||');
          const dept  = (parts[2] || '').trim() || 'Unassigned';
          const rev   = parseFloat(parts[3]) || 0;
          deptMap[dept]  = (deptMap[dept]  || 0) + rev;
          total_revenue += rev;
        });
      } else {
        // Fallback for jobs without billing lines
        const dept = j.department || 'Unassigned';
        const rev  = j.revenue_full || 0;
        deptMap[dept]  = (deptMap[dept]  || 0) + rev;
        total_revenue += rev;
      }
    });

    const gst           = total_revenue * 0.1;
    const by_department = Object.entries(deptMap)
      .map(([department, revenue]) => ({ department, revenue }))
      .sort((a, b) => b.revenue - a.revenue);
    return { total_jobs, total_revenue, total_payout, gst, by_department };
  }, [filteredJobs, singleStaffId]);

  // ── Weekly financial report data ─────────────────────────────────────────
  const weeklyReport = useMemo(() => {
    if (viewMode !== 'week' || filterDate || !summary) return null;
    const gross      = dynamicSummary.total_revenue;
    // Add contractor_extras (e.g. Accounting Assistance) on top of job-level payouts.
    // These are returned from the backend in by_employee[].extras_total.
    const extrasTotal = (summary.by_employee || []).reduce(
      (s, e) => s + (parseFloat(e.extras_total) || 0), 0
    );
    const staff    = dynamicSummary.total_payout + extrasTotal;
    const net      = gross - staff;
    const gst      = gross * 0.1;
    const tax      = net * 0.15;
    const savings  = gst + tax;
    const staffPct = gross > 0 ? (staff / gross) * 100 : 0;
    const netPct   = gross > 0 ? (net   / gross) * 100 : 0;
    const { week: wkNum } = getISOWeek(weekStart);
    const employees    = summary.by_employee || [];
    const commissioned = employees.filter(e => e.type === 'commissioned');
    const allHourly    = employees.filter(e => e.type === 'hourly');
    // Fixed-rate: have hourly_rate set (Fellipe, Gabriel) — show days×rate reconciliation
    // Payout-based: no hourly_rate (Rafael) — show total payout like commissioned
    const hourly       = allHourly.filter(e => e.hourly_rate && parseFloat(e.hourly_rate) > 0);
    const payoutHourly = allHourly.filter(e => !e.hourly_rate || parseFloat(e.hourly_rate) === 0);
    const oc           = employees.filter(e => e.type === 'office_cleaner');
    const michelPersonal = employees.find(e => e.username === 'michel_personal');
    const michelAdmin    = employees.find(e => e.username === 'admin');
    const cars = (michelPersonal ? parseFloat(michelPersonal.total_payout) : 0)
               + (michelAdmin    ? parseFloat(michelAdmin.total_payout)    : 0);
    const michelGross    = cars + net / 3;
    const michelTax      = michelGross * 0.10;
    const michelNet      = michelGross - michelTax;
    const michelTithe    = michelNet * 0.10;
    // The director's salary is now taken as a DIVIDEND (paid FROM profit), so
    // it is no longer deducted from Net Profit. Company tax is estimated at 25%
    // of the full net profit. directorShare stays only for the Director Salary
    // card below.
    const directorShare  = net / 3;
    const trueNetProfit  = net - weeklyExpenses;
    const taxReserve     = trueNetProfit * 0.25;
    return { gross, staff, net, gst, tax, savings, staffPct, netPct, wkNum,
             commissioned, hourly, payoutHourly, oc, cars, michelGross, michelTax, michelNet, michelTithe,
             directorShare, trueNetProfit, taxReserve };
  }, [viewMode, filterDate, summary, dynamicSummary, weekStart, weeklyExpenses]); // weeklyExpenses from statement API

  // ── Period label for department breakdown header ──────────────────────────
  const periodLabel = useMemo(() => {
    if (filterDate) {
      const [y, m, d] = filterDate.split('-');
      return `${d}/${m}/${y}`;
    }
    if (viewMode === 'week') {
      return weekLabel(weekStart);
    }
    if (activeYears.length > 0) {
      return activeYears.slice().sort().join(', ');
    }
    return 'All Periods';
  }, [filterDate, viewMode, weekStart, activeYears]);

  // Compute totals from visible billing lines (respects service filter per line)
  const totals = useMemo(() => filteredJobs.reduce((acc, j) => {
    // Parse billing lines inline (same logic as parseBillingLines)
    const allLines = j.dept_lines
      ? j.dept_lines.split(';;;').map(entry => {
          const p = entry.split('|||');
          return { lineId: parseInt(p[0])||null, service: p[1]||'', department: p[2]||'', revenue_full: parseFloat(p[3])||0, revenue_pay: parseFloat(p[4])||0 };
        })
      : [{ lineId: null, service: j.job_description||'', department: j.department||'', revenue_full: j.revenue_full||0, revenue_pay: j.revenue_pay||0 }];

    // When service / dept filters active, only count matching billing lines
    let visible = allLines;
    if (filterService.size > 0) visible = visible.filter(bl => filterService.has(bl.service));
    if (filterDept.size > 0)    visible = visible.filter(bl => filterDept.has(bl.department));
    if (visible.length === 0) return acc;

    const lineRev = visible.reduce((s, bl) => s + bl.revenue_full, 0);
    const linePay = visible.reduce((s, bl) => s + bl.revenue_pay, 0);
    const totalLinePay = allLines.reduce((s, bl) => s + bl.revenue_pay, 0);

    let payout;
    if (singleStaffId) {
      const empPayout = getEmployeePayout(j, singleStaffId);
      payout = totalLinePay > 0 ? empPayout * (linePay / totalLinePay) : 0;
    } else {
      payout = linePay;
    }

    return {
      revenue: acc.revenue + lineRev,
      gst:     acc.gst     + lineRev * 0.1,
      payout:  acc.payout  + payout,
      count:   acc.count   + visible.length,
    };
  }, { revenue: 0, gst: 0, payout: 0, count: 0 }), [filteredJobs, singleStaffId, filterService, filterDept]);

  // One margin percentage, one precision — the KPI strip and the footer used
  // to disagree (32.2% vs 32%).
  const marginPct = totals.revenue > 0
    ? (((totals.revenue - totals.payout) / totals.revenue) * 100).toFixed(1)
    : '0.0';

  // Week range info for display
  const { from: weekFrom, to: weekTo } = useMemo(() => getWeekRange(weekStart), [weekStart]);

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: 16 }}>

      {/* ── Overview section (stats + payroll + dept breakdown) ── */}
      {view !== 'jobs' && (<>

      {/* Birthday banner — shows tomorrow's birthdays as a heads-up and
          today's birthdays as a "celebrate now" reminder. Hidden when no
          active contractor has a birthday in the next two days. */}
      {birthdays.length > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, #FCE7F3 0%, #FBCFE8 100%)',
          border: '1px solid #F472B6', borderRadius: 12,
          padding: '12px 16px', marginBottom: 16, marginTop: 4,
          display: 'flex', alignItems: 'center', gap: 12, fontSize: 14,
        }}>
          <Cake size={26} weight="fill" color="#BE185D" />
          <div style={{ flex: 1 }}>
            <strong style={{ color: '#9D174D' }}>
              {birthdays.length === 1 ? 'Aniversário ' : 'Aniversários '}
            </strong>
            <span style={{ color: '#831843' }}>
              {birthdays.map((b, i) => (
                <React.Fragment key={b.id}>
                  <strong>{b.name}</strong>
                  <span style={{ fontSize: 12, marginLeft: 4, color: b.when === 'today' ? '#BE185D' : '#A21CAF', fontWeight: 700 }}>
                    {b.when === 'today' ? '🎉 hoje' : 'amanhã'}
                  </span>
                  {i < birthdays.length - 1 && <span style={{ color: '#9D174D' }}> · </span>}
                </React.Fragment>
              ))}
            </span>
            <div style={{ fontSize: 12, color: '#9D174D', marginTop: 2 }}>
              {birthdays.some(b => b.when === 'tomorrow') && 'Amanhã '}
              {birthdays.some(b => b.when === 'tomorrow') && birthdays.some(b => b.when === 'today') && '· '}
              {birthdays.some(b => b.when === 'today') && 'Today '}
              — não esquece de mandar um parabéns!
            </div>
          </div>
        </div>
      )}

      {/* Public holiday banner */}
      {todayHoliday && (
        <div style={{
          background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 12,
          padding: '12px 16px', marginBottom: 16, marginTop: 4,
          display: 'flex', alignItems: 'center', gap: 12, fontSize: 14,
        }}>
          <Island size={22} weight="fill" color="#92400e" />
          <div>
            <strong style={{ color: '#78350F' }}>Today is a WA Public Holiday</strong>
            {' '}— <span style={{ color: '#92400e' }}>{todayHoliday.name}</span>
            <div style={{ fontSize: 12, color: '#92400e', marginTop: 1 }}>
              Daily contracts are automatically cancelled for this day.
            </div>
          </div>
        </div>
      )}

      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 4, marginBottom: 16, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0F2233', letterSpacing: -0.3 }}>
          Dashboard
        </h2>
        <span style={{ background: '#0F2233', color: '#F5C518', fontWeight: 700, padding: '4px 12px', borderRadius: 999, fontSize: 12, letterSpacing: 0.3 }}>
          {formatDateDMY(today)}
        </span>
      </div>

      {/* ── Week selector (Dashboard/overview mode only) ── */}
      {view === 'overview' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
          <button onClick={() => setWeekStart(s => shiftWeek(s, -1))}
            style={{ width: 36, height: 36, border: '1px solid #E5E7EB', borderRadius: 10, fontSize: 16, cursor: 'pointer', background: '#fff', color: '#0F2233', fontWeight: 700, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>
          <div style={{ textAlign: 'center', minWidth: 240, background: '#fff', padding: '8px 16px', border: '1px solid #EEF1F4', borderRadius: 12, boxShadow: '0 1px 3px rgba(15,34,51,0.04)' }}>
            <div style={{ fontWeight: 800, fontSize: 14, color: '#0F2233' }}>{weekLabel(weekStart)}</div>
            <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 2 }}>{weekRangeLabel(weekFrom, weekTo)}</div>
          </div>
          <button onClick={() => setWeekStart(s => shiftWeek(s, 1))}
            style={{ width: 36, height: 36, border: '1px solid #E5E7EB', borderRadius: 10, fontSize: 16, cursor: 'pointer', background: '#fff', color: '#0F2233', fontWeight: 700, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>
          {weekStart !== thisWeekStart() && (
            <button onClick={() => setWeekStart(thisWeekStart())}
              style={{ padding: '8px 14px', border: 'none', borderRadius: 999, fontSize: 11, cursor: 'pointer', background: '#4A7A91', color: '#fff', fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase' }}>
              This Week
            </button>
          )}
        </div>
      )}

      {/* ── Weekly Financial Report (week mode) / Simple summary (other modes) ── */}
      {weeklyReport ? (
        <div style={{ marginBottom: 16 }}>

          {/* ── 6 KPI metric cards — single row, compact ── */}
          <div className="v2-kpi-row">

            {/* 1 — Gross Revenue */}
            <div className="v2-kpi v2-kpi-compact">
              <div className="v2-kpi-icon" style={{ background: '#FEF3C7' }}>
                <CurrencyDollar size={20} color="#D97706" weight="fill" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="v2-kpi-label">Gross</div>
                <div className="v2-kpi-value">${weeklyReport.gross.toFixed(2)}</div>
                <div className="v2-kpi-sub">
                  <span style={{ color: '#22C55E', fontWeight: 700 }}>${(weeklyReport.gross + weeklyReport.gst).toFixed(2)}</span>
                  <span style={{ color: '#94A3B8' }}> inc GST</span>
                </div>
              </div>
            </div>

            {/* 2 — Net Revenue */}
            <div className="v2-kpi v2-kpi-compact">
              <div className="v2-kpi-icon" style={{ background: '#DCFCE7' }}>
                <TrendUp size={20} color="#16A34A" weight="fill" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="v2-kpi-label">Net Rev</div>
                <div className="v2-kpi-value">${weeklyReport.net.toFixed(2)}</div>
                <div className="v2-kpi-sub">
                  <span style={{ color: '#22C55E', fontWeight: 700 }}>{weeklyReport.netPct.toFixed(1)}%</span>
                  <span style={{ color: '#94A3B8' }}> gross</span>
                </div>
              </div>
            </div>

            {/* 3 — True Net Profit (dark hero card) */}
            <div className="v2-kpi v2-kpi-compact" style={{ background: 'linear-gradient(140deg, #1E3A4A 0%, #0F2233 100%)', border: 'none', boxShadow: '0 4px 16px rgba(15,35,51,0.35)' }}>
              <div className="v2-kpi-icon" style={{ background: 'rgba(245,197,24,0.18)' }}>
                <Coin size={20} color="#F5C518" weight="fill" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="v2-kpi-label" style={{ color: '#F5C518' }}>Net Pro</div>
                <div className="v2-kpi-value" style={{ color: weeklyReport.trueNetProfit >= 0 ? '#fff' : '#FCA5A5' }}>
                  ${weeklyReport.trueNetProfit.toFixed(2)}
                </div>
                <div className="v2-kpi-sub" style={{ color: '#94A3B8' }}>
                  <span style={{ color: '#F5C518', fontWeight: 700 }}>
                    {weeklyReport.gross > 0 ? ((weeklyReport.trueNetProfit / weeklyReport.gross) * 100).toFixed(1) : 0}%
                  </span>
                  <span style={{ color: '#64748B' }}> gross</span>
                </div>
              </div>
            </div>

            {/* 4 — GST */}
            <div className="v2-kpi v2-kpi-compact">
              <div className="v2-kpi-icon" style={{ background: '#DBEAFE' }}>
                <Percent size={20} color="#2563EB" weight="fill" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="v2-kpi-label">GST</div>
                <div className="v2-kpi-value">${weeklyReport.gst.toFixed(2)}</div>
                <div className="v2-kpi-sub">
                  <span style={{ color: '#3B82F6', fontWeight: 700 }}>10%</span>
                  <span style={{ color: '#94A3B8' }}> gross</span>
                </div>
              </div>
            </div>

            {/* 5 — TAX (25% of net profit) */}
            <div className="v2-kpi v2-kpi-compact">
              <div className="v2-kpi-icon" style={{ background: '#FEF3C7' }}>
                <Receipt size={20} color="#D97706" weight="fill" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="v2-kpi-label">Tax</div>
                <div className="v2-kpi-value">${weeklyReport.taxReserve.toFixed(2)}</div>
                <div className="v2-kpi-sub">
                  <span style={{ color: '#F59E0B', fontWeight: 700 }}>25%</span>
                  <span style={{ color: '#94A3B8' }}> net pro</span>
                </div>
              </div>
            </div>

            {/* 6 — Staff Payout */}
            <div className="v2-kpi v2-kpi-compact">
              <div className="v2-kpi-icon" style={{ background: '#FEE2E2' }}>
                <UsersThree size={20} color="#DC2626" weight="fill" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="v2-kpi-label">Staff</div>
                <div className="v2-kpi-value">${weeklyReport.staff.toFixed(2)}</div>
                <div className="v2-kpi-sub">
                  <span style={{ color: '#EF4444', fontWeight: 700 }}>{weeklyReport.staffPct.toFixed(1)}%</span>
                  <span style={{ color: '#94A3B8' }}> gross</span>
                </div>
              </div>
            </div>

          </div>

          {/* ── Payroll + Director Salary ── */}
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.8fr) minmax(0,1fr)', gap: 16, alignItems: 'start' }}>

            {/* LEFT: Payroll */}
            <div className="v2-card-lg">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0F172A', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                  Payroll — Week {weeklyReport.wkNum}
                </div>
              </div>

              {/* All payout-based workers in one row (commissioned + OC + payoutHourly) */}
              {(() => {
                const workers = [
                  ...weeklyReport.commissioned.map(e => ({ ...e, label: e.name.split(' ')[0], sublabel: null })),
                  ...weeklyReport.oc.map(e => ({ ...e, label: e.name.split(' ')[0], sublabel: 'Office' })),
                  ...weeklyReport.payoutHourly.map(e => ({ ...e, label: e.name.split(' ')[0], sublabel: null })),
                ];
                if (workers.length === 0) return null;
                return (
                  <div className="v2-payroll-row" style={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${workers.length}, minmax(0, 1fr))`,
                    gap: 8, marginBottom: 14,
                  }}>
                    {workers.map(e => {
                      const extAmt = parseFloat(e.extras_total) || 0;
                      return (
                        <div key={e.id} style={{
                          background: '#F8FAFC', border: '1px solid #E5E7EB',
                          borderRadius: 12, padding: '10px 10px',
                          display: 'flex', flexDirection: 'column', alignItems: 'center',
                          gap: 6, minWidth: 0, textAlign: 'center',
                        }}>
                          {/* Avatar: photo or user icon fallback */}
                          <div style={{ width: 40, height: 40, borderRadius: '50%', flexShrink: 0, background: e.photo_url ? `#E5E7EB url(${e.photo_url}) center/cover no-repeat` : '#E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {!e.photo_url && <User size={20} color="#94A3B8" weight="fill" />}
                          </div>
                          <div style={{ fontSize: 11, fontWeight: 700, color: '#334155', display: 'flex', alignItems: 'center', gap: 4, maxWidth: '100%' }}>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.label}</span>
                            {e.sublabel && (
                              <span className="v2-pill v2-pill-blue" style={{ padding: '1px 5px', fontSize: 8 }}>
                                {e.sublabel}
                              </span>
                            )}
                          </div>
                          <div style={{ fontSize: 14, fontWeight: 800, color: '#0F172A', letterSpacing: -0.3 }}>${parseFloat(e.total_payout).toFixed(2)}</div>
                          {/* Show extras whether they're a bonus (+) OR a deduction (−).
                              extras_total comes from contractor_extras and can be negative
                              when the boss applies a discount. Hiding negatives made the
                              total look unexplained ("why is Caua $1161 when his base is
                              $1411?"). */}
                          {extAmt !== 0 && (
                            <div style={{
                              fontSize: 9,
                              color: extAmt > 0 ? '#22C55E' : '#DC2626',
                              fontWeight: 700,
                            }}>
                              {extAmt > 0 ? '+' : '−'}${Math.abs(extAmt).toFixed(2)}
                            </div>
                          )}
                          {/* Office cleaners: show how many days the payout covers so it's
                              obvious when the figure looks small ("$100 = 1 day, not a bug"). */}
                          {e.type === 'office_cleaner' && e.days_worked != null && (
                            <div style={{ fontSize: 9, color: '#64748B', fontWeight: 600 }}>
                              {e.days_worked} day{Number(e.days_worked) === 1 ? '' : 's'}
                            </div>
                          )}
                          {/* Today's running total — only shows when the selected period
                              actually contains today (backend nulls out today_iso otherwise),
                              so looking at "Week 17" doesn't pretend today's pay belongs there. */}
                          {summary?.today_iso && parseFloat(e.today_payout || 0) > 0 && (
                            <div
                              title={`Earned today (${summary.today_iso}) — already included in the week total above`}
                              style={{
                                marginTop: 2, fontSize: 9, fontWeight: 700, color: '#1E40AF',
                                background: '#DBEAFE', padding: '1px 6px', borderRadius: 999,
                                display: 'inline-block',
                              }}
                            >
                              ⏱ +${parseFloat(e.today_payout).toFixed(2)} today
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              {/* Hourly fixed-rate (Fellipe, Gabriel) */}
              {weeklyReport.hourly.length > 0 && (
                <div style={{ background: '#F8FAFC', borderRadius: 12, padding: '12px 14px', border: '1px solid #E5E7EB' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>
                    Hourly — Fixed Rate
                  </div>
                  {weeklyReport.hourly.map(e => {
                    const dbDays   = parseFloat(e.days_worked) || 0;
                    const days     = hourlyDaysOverride[e.id] !== undefined ? hourlyDaysOverride[e.id] : dbDays;
                    const rate     = parseFloat(e.hourly_rate) || 28.50;
                    const dailyPay = +(rate * 8).toFixed(2);
                    const calc     = days * dailyPay;
                    const actual   = parseFloat(e.total_payout) || 0;
                    const diff     = actual - calc;
                    const isOverridden = hourlyDaysOverride[e.id] !== undefined && hourlyDaysOverride[e.id] !== dbDays;
                    return (
                      <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: '1px solid #F1F5F9', flexWrap: 'wrap' }}>
                        <div style={{ width: 28, height: 28, borderRadius: '50%', flexShrink: 0, background: e.photo_url ? `#E5E7EB url(${e.photo_url}) center/cover no-repeat` : '#E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {!e.photo_url && <User size={14} color="#94A3B8" weight="fill" />}
                        </div>
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#334155', width: 70, flexShrink: 0 }}>{e.name.split(' ')[0]}</span>
                        <input
                          type="number" min="0" max="7" step="any"
                          value={Number.isFinite(days) ? +days.toFixed(2) : days}
                          onChange={ev => setHourlyDays(e.id, parseFloat(ev.target.value) || 0)}
                          title="Edit days — accepts any fraction (e.g. 1.73 = 13h 49m)"
                          style={{
                            width: 64, border: '1px solid ' + (isOverridden ? '#F59E0B' : '#E5E7EB'),
                            borderRadius: 8, padding: '3px 6px', fontSize: 12, fontWeight: 700,
                            color: isOverridden ? '#92400E' : '#334155',
                            background: isOverridden ? '#FEF3C7' : '#fff',
                            textAlign: 'center',
                          }}
                        />
                        {/* Hours-and-minutes hint right after the input so the
                            boss can read "13h 49m" instead of decoding 1.73. */}
                        <span style={{ fontSize: 10, color: '#94A3B8', whiteSpace: 'nowrap' }}>
                          ({fmtHM(days * 8)})
                        </span>
                        {/* calc = what's PAID (days × dailyPay).
                            actual = REVENUE the contractor generated through
                            the jobs they worked on (sum of je.payout). diff
                            = profit/loss for the company — green when their
                            jobs out-earn their salary, red when not. */}
                        <span style={{ fontSize: 11, color: '#94A3B8' }}>× ${dailyPay.toFixed(2)}/d = <strong style={{ color: '#334155' }}>${calc.toFixed(2)}</strong></span>
                        <span style={{ fontSize: 11, color: '#94A3B8' }}>actual <strong style={{ color: '#334155' }}>${actual.toFixed(2)}</strong></span>
                        <span style={{ fontSize: 12, fontWeight: 800, color: diff >= 0 ? '#22C55E' : '#EF4444', marginLeft: 'auto' }}>
                          {diff >= 0 ? '+' : ''}{diff.toFixed(2)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Total */}
              <div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#F8FAFC', padding: '14px 16px', borderRadius: 12, border: '1px solid #E5E7EB' }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: 1 }}>Total Payroll</span>
                <span style={{ fontSize: 22, fontWeight: 900, color: '#4A7A91', letterSpacing: -0.5 }}>${weeklyReport.staff.toFixed(2)}</span>
              </div>

              {/* Today's running payroll — sums the today_payout fields so the
                  boss sees how much of the weekly total is already locked in
                  for the current day. Only shows when the period actually
                  contains today. */}
              {summary?.today_iso && (() => {
                const todayTotal = (summary.by_employee || []).reduce(
                  (s, e) => s + (parseFloat(e.today_payout) || 0), 0);
                if (todayTotal <= 0) return null;
                return (
                  <div style={{
                    marginTop: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    background: '#DBEAFE', padding: '10px 16px', borderRadius: 12, border: '1px solid #93C5FD',
                  }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#1E40AF', textTransform: 'uppercase', letterSpacing: 1 }}>
                      ⏱ Today ({summary.today_iso.split('-').reverse().join('/')})
                    </span>
                    <span style={{ fontSize: 16, fontWeight: 800, color: '#1E40AF', letterSpacing: -0.3 }}>
                      ${todayTotal.toFixed(2)}
                    </span>
                  </div>
                );
              })()}
            </div>

            {/* RIGHT: Director Salary — dark rounded card */}
            <div style={{ background: 'linear-gradient(155deg, #1E3A4A 0%, #0F2233 100%)', borderRadius: 16, padding: '20px 22px', color: '#fff', boxShadow: '0 4px 16px rgba(15,35,51,0.25)' }}>
              <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color: '#F5C518', marginBottom: 16 }}>
                Director Salary — W{weeklyReport.wkNum}
              </div>

              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: '#94A3B8', marginBottom: 2 }}>Cars (personal jobs)</div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#fff', letterSpacing: -0.5 }}>${weeklyReport.cars.toFixed(2)}</div>
              </div>

              <div style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <div style={{ fontSize: 11, color: '#94A3B8', marginBottom: 2 }}>+ 1/3 Net Revenue</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ fontSize: 11, color: '#64748B' }}>1/3 × ${weeklyReport.net.toFixed(2)}</span>
                  <span style={{ fontSize: 18, fontWeight: 800, color: '#fff', letterSpacing: -0.5 }}>${(weeklyReport.net / 3).toFixed(2)}</span>
                </div>
              </div>

              <div style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                <div style={{ fontSize: 11, color: '#94A3B8', marginBottom: 2 }}>Gross Salary</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: '#F5C518', letterSpacing: -0.5 }}>${weeklyReport.michelGross.toFixed(2)}</div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10, fontSize: 12 }}>
                <span style={{ color: '#94A3B8' }}>Tax (10%)</span>
                <span style={{ color: '#EF4444', fontWeight: 700 }}>−${weeklyReport.michelTax.toFixed(2)}</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.08)', marginBottom: 12 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: '#E2E8F0' }}>Net Salary</span>
                <span style={{ fontSize: 20, fontWeight: 900, color: '#fff', letterSpacing: -0.5 }}>${weeklyReport.michelNet.toFixed(2)}</span>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(245,197,24,0.12)', borderRadius: 10, padding: '10px 14px' }}>
                <span style={{ fontSize: 11, color: '#CBD5E1', fontWeight: 600 }}>Tithe 10% (dízimo)</span>
                <span style={{ fontSize: 15, fontWeight: 900, color: '#F5C518' }}>${weeklyReport.michelTithe.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Simple summary cards for year/research/date filter modes */
        <div className="summary-cards">
          <div className="summary-card">
            <div className="label">Total Jobs</div>
            <div className="value blue">{dynamicSummary.total_jobs}</div>
          </div>
          <div className="summary-card">
            <div className="label">Revenue (ex GST)</div>
            <div className="value">${dynamicSummary.total_revenue.toFixed(2)}</div>
          </div>
          <div className="summary-card">
            <div className="label">GST (10%)</div>
            <div className="value" style={{ color: '#6b7280' }}>${dynamicSummary.gst.toFixed(2)}</div>
          </div>
          <div className="summary-card">
            <div className="label">Total inc GST</div>
            <div className="value" style={{ color: '#059669', fontWeight: 800 }}>
              ${(dynamicSummary.total_revenue * 1.1).toFixed(2)}
            </div>
          </div>
          <div className="summary-card">
            <div className="label">{singleStaff ? `${singleStaff.split(' ')[0]}'s Payout` : 'Comm. Payout'}</div>
            <div className="value" style={{ color: '#dc2626' }}>${dynamicSummary.total_payout.toFixed(2)}</div>
          </div>
          <div className="summary-card">
            <div className="label">Margin (ex GST)</div>
            <div className="value" style={{ color: (dynamicSummary.total_revenue - dynamicSummary.total_payout) >= 0 ? '#16a34a' : '#dc2626' }}>
              ${(dynamicSummary.total_revenue - dynamicSummary.total_payout).toFixed(2)}
            </div>
          </div>
          {summary && (
            <div className="summary-card">
              <div className="label">Labour Cost</div>
              <div className="value" style={{ color: '#dc2626' }}>${(summary.hourly_labor_cost || 0).toFixed(2)}</div>
            </div>
          )}
        </div>
      )}

      {/* Coverage panels are injected here (Dashboard home) so "who is covering
          their day rate" sits right under Payroll instead of ~2000px down. */}
      {view !== 'jobs' && slotAfterPayroll}

      {/* Department revenue breakdown — v2 rounded + colored icons */}
      {dynamicSummary.by_department.length > 0 && (
        <div className="v2-card-lg" style={{ marginBottom: 16 }}>
          {/* ── Header line: title + period + totals ── */}
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 14, marginBottom: 16 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#0F2233', letterSpacing: -0.2 }}>
              Revenue by Department
            </h3>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#4A7A91', background: '#E8F4F8', padding: '4px 10px', borderRadius: 999, whiteSpace: 'nowrap' }}>
              {periodLabel}
            </span>
            <div style={{ flex: 1 }} />
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: '#6B7280', whiteSpace: 'nowrap' }}>
                ex GST <strong style={{ fontSize: 15, color: '#0F2233', fontWeight: 800 }}>${dynamicSummary.total_revenue.toFixed(2)}</strong>
              </span>
              <span style={{ color: '#E5E7EB' }}>|</span>
              <span style={{ fontSize: 12, color: '#6B7280', whiteSpace: 'nowrap' }}>
                inc GST <strong style={{ fontSize: 16, color: '#15803D', fontWeight: 800 }}>${(dynamicSummary.total_revenue * 1.1).toFixed(2)}</strong>
              </span>
              <span style={{ fontSize: 11, color: '#9CA3AF', whiteSpace: 'nowrap' }}>
                · {dynamicSummary.total_jobs} job{dynamicSummary.total_jobs !== 1 ? 's' : ''}
              </span>
            </div>
          </div>

          {(() => {
            // Fixed display order: OFFICE → SERVICE → USED CARS → KIA CARS → SUZUKI CARS → GWM CARS
            const DEPT_ORDER = ['OFFICE', 'SERVICE', 'USED CARS', 'KIA CARS', 'SUZUKI CARS', 'GWM CARS', 'GAC CARS'];
            const byName = {};
            dynamicSummary.by_department.forEach(d => { byName[d.department] = d; });
            const ordered = DEPT_ORDER
              .map(name => byName[name])
              .filter(Boolean)
              .concat(dynamicSummary.by_department.filter(d => !DEPT_ORDER.includes(d.department)));

            return (
              <div className="v2-dept-row">
                {ordered.map(d => {
                  const pct = dynamicSummary.total_revenue > 0
                    ? (d.revenue / dynamicSummary.total_revenue) * 100
                    : 0;
                  const di = DEPT_ICONS[d.department] || DEPT_ICONS['Unassigned'];
                  const DeptIcon = di.Icon;
                  return (
                    <div key={d.department} className="v2-dept-card">
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                        <div style={{
                          width: 34, height: 34, borderRadius: '50%',
                          background: di.bg, color: di.color,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0,
                        }}>
                          <DeptIcon size={18} weight="fill" />
                        </div>
                        <div style={{ fontSize: 10, fontWeight: 700, color: '#0F2233', letterSpacing: 0.3, textTransform: 'uppercase', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {d.department}
                        </div>
                      </div>

                      <div style={{ fontSize: 16, fontWeight: 800, color: '#0F2233', letterSpacing: -0.3, lineHeight: 1.1 }}>
                        ${(d.revenue || 0).toFixed(2)}
                      </div>
                      <div style={{ fontSize: 9, color: '#9CA3AF', marginTop: 2 }}>
                        inc ${(d.revenue * 1.1).toFixed(2)}
                      </div>

                      <div style={{ marginTop: 8, height: 5, background: '#F1F5F9', borderRadius: 999, overflow: 'hidden' }}>
                        <div style={{
                          width: `${pct}%`, height: '100%', background: di.color,
                          borderRadius: 999, transition: 'width 0.5s ease',
                        }} />
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 800, color: di.color, marginTop: 4 }}>
                        {pct.toFixed(1)}%
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      </>)}

      {/* ── Jobs section (filters + table) ── */}
      {view !== 'overview' && (<>

      {/* Sticky filters bar */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 50, background: '#fff',
        borderBottom: '1px solid #e5e7eb', padding: '8px 0', marginBottom: 12,
      }}>

        {/* ── Row 1: date filter ── */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 6 }}>

          {/* Mode toggle — Week / Year / All (always visible) */}
          <div style={{ display: 'flex', border: '1px solid #d1d5db', borderRadius: 0, overflow: 'hidden', flexShrink: 0 }}>
            <button
              onClick={() => { setViewMode('week'); setFilterDate(''); }}
              style={{ padding: '5px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', display: 'flex', alignItems: 'center', gap: 5,
                background: viewMode === 'week' ? '#1e3a4a' : '#fff',
                color:      viewMode === 'week' ? '#fff'    : '#6b7280' }}
            ><CalendarBlank size={13} weight="duotone" /> Week</button>
            <button
              onClick={() => { setViewMode('year'); setFilterDate(''); if (filterYears.size === 0) setFilterYears(new Set([String(currentYear)])); }}
              style={{ padding: '5px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', borderLeft: '1px solid #d1d5db', display: 'flex', alignItems: 'center', gap: 5,
                background: viewMode === 'year' ? '#1e3a4a' : '#fff',
                color:      viewMode === 'year' ? '#fff'    : '#6b7280' }}
            ><MagnifyingGlass size={13} weight="duotone" /> Year</button>
            <button
              onClick={() => { setViewMode('all'); setFilterDate(''); }}
              style={{ padding: '5px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', border: 'none', borderLeft: '1px solid #d1d5db',
                background: viewMode === 'all' ? '#1e3a4a' : '#fff',
                color:      viewMode === 'all' ? '#fff'    : '#6b7280' }}
            >All</button>
          </div>

          {viewMode === 'week' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
              <button onClick={() => setWeekStart(s => shiftWeek(s, -1))}
                style={{ padding: '4px 10px', border: '1px solid #d1d5db', borderRadius: 0, fontSize: 13, cursor: 'pointer', background: '#fff' }}>‹</button>
              <div style={{ textAlign: 'center', minWidth: 240 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: '#1f2937' }}>{weekLabel(weekStart)}</div>
                <div style={{ fontSize: 11, color: '#6b7280' }}>{weekRangeLabel(weekFrom, weekTo)}</div>
              </div>
              <button onClick={() => setWeekStart(s => shiftWeek(s, 1))}
                style={{ padding: '4px 10px', border: '1px solid #d1d5db', borderRadius: 0, fontSize: 13, cursor: 'pointer', background: '#fff' }}>›</button>
              {weekStart !== thisWeekStart() && (
                <button onClick={() => setWeekStart(thisWeekStart())}
                  style={{ padding: '4px 9px', border: '1px solid #4A7A91', borderRadius: 0, fontSize: 11, cursor: 'pointer', background: '#e8f2f7', color: '#4A7A91', fontWeight: 600 }}>This Week</button>
              )}
            </div>
          )}

          {viewMode === 'year' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 12, color: '#6b7280' }}>Year:</span>
              <ExcelFilter label="Year" options={yearOptions} selected={filterYears} onChange={setFilterYears} width={150} />
              <span style={{ fontSize: 11, color: '#9ca3af' }}>Loads all jobs for selected year(s)</span>
            </div>
          )}

          {viewMode === 'all' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 11, color: '#9ca3af' }}>
                {hasFilter ? 'Searching all history' : 'Type a filter to search'}
              </span>
            </div>
          )}

          {/* Quick date picker */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: view === 'jobs' ? 0 : 'auto' }}>
            <input
              type="date" value={filterDate}
              onChange={e => setFilterDate(e.target.value)}
              style={{ padding: '4px 7px', border: filterDate ? '1.5px solid #4A7A91' : '1px solid #d1d5db', borderRadius: 0, fontSize: 12 }}
            />
            {filterDate && (
              <button onClick={() => setFilterDate('')}
                style={{ padding: '3px 7px', border: '1px solid #e5e7eb', borderRadius: 0, fontSize: 11, cursor: 'pointer', background: 'white' }}>✕</button>
            )}
            <button onClick={() => setFilterDate(today)}
              style={{ padding: '4px 8px', border: '1px solid #e5e7eb', borderRadius: 0, fontSize: 11, cursor: 'pointer', background: 'white', color: '#6b7280' }}>Today</button>
          </div>
        </div>

        {/* ── Row 2: Excel column filters ── */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>

          {/* Stock number search */}
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <MagnifyingGlass size={13} color="#98a2b3" style={{ position: 'absolute', left: 7, pointerEvents: 'none' }} />
            <input
              type="text"
              placeholder="Stock #"
              value={filterStock}
              onChange={e => setFilterStock(e.target.value)}
              style={{
                paddingLeft: 24, paddingRight: filterStock ? 22 : 8, paddingTop: 4, paddingBottom: 4,
                border: filterStock ? '1.5px solid #4A7A91' : '1px solid #d1d5db',
                borderRadius: 0, fontSize: 12, width: 110,
              }}
            />
            {filterStock && (
              <span onClick={() => setFilterStock('')} style={{ position: 'absolute', right: 6, cursor: 'pointer', color: '#9ca3af', fontSize: 12 }}>✕</span>
            )}
          </div>

          <ExcelFilter label="Model"   options={modelOptions}   selected={filterModel}   onChange={setFilterModel}   width={160} />
          <ExcelFilter label="Staff"   options={staffOptions}   selected={filterStaff}   onChange={setFilterStaff}   width={180} />
          <ExcelFilter label="Status"  options={statusOptions}  selected={filterStatus}  onChange={setFilterStatus}  width={150} />
          <ExcelFilter label="Dept"    options={deptOptions}    selected={filterDept}    onChange={setFilterDept}    width={160} />
          <ExcelFilter label="Service" options={serviceOptions} selected={filterService} onChange={setFilterService} width={220} />

          {hasActiveFilter && (
            <button onClick={clearAllFilters} style={{
              padding: '4px 9px', border: '1px solid #dc2626', borderRadius: 0,
              fontSize: 11, fontWeight: 600, cursor: 'pointer', background: 'transparent', color: '#dc2626',
            }}>✕ Clear All</button>
          )}

          <div style={{ display: 'flex', gap: 6, marginLeft: 'auto' }}>
            <button onClick={() => {
              const p = filterDate ? { date: filterDate }
                : viewMode === 'week' ? (() => { const { week: wn, year: wy } = getISOWeek(weekStart); return { week_number: wn, week_year: wy }; })()
                : viewMode === 'year' && filterYears.size > 0 ? { year: [...filterYears].join(',') }
                : {};
              api.exportJobs(p);
            }}
              style={{
                padding: '4px 9px', border: '1px solid #d1d5db', borderRadius: 0,
                fontSize: 11, cursor: 'pointer', background: 'transparent', color: '#6b7280',
              }}>Export CSV</button>

            <button onClick={() => {
              const p = filterDate ? { date: filterDate }
                : viewMode === 'week' ? (() => { const { week: wn, year: wy } = getISOWeek(weekStart); return { week_number: wn, week_year: wy }; })()
                : viewMode === 'year' && filterYears.size > 0 ? { year: [...filterYears].join(',') }
                : {};
              api.exportBackup(p);
            }}
              style={{
                padding: '4px 9px', border: '1px solid #d1d5db', borderRadius: 0,
                fontSize: 11, cursor: 'pointer', background: '#f2f4f6', color: '#475467', fontWeight: 700,
              }}>Backup Excel</button>
          </div>
        </div>

        {/* Active filter summary */}
        {hasActiveFilter && (
          <div style={{ marginTop: 4, fontSize: 11, color: '#6b7280', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {filterDate && <span style={{ background: '#e8f2f7', borderRadius: 0, padding: '1px 6px', display:'inline-flex', alignItems:'center', gap:3 }}><CalendarBlank size={11} weight="duotone" />{formatDateDMY(filterDate)}</span>}
            {filterStock.trim() && <span style={{ background: '#fef3c7', borderRadius: 0, padding: '1px 6px' }}>🔎 Stock: {filterStock}</span>}
            {filterModel.size > 0 && <span style={{ background: '#fef3c7', borderRadius: 0, padding: '1px 6px' }}>Model: {[...filterModel].join(', ')}</span>}
            {filterStaff.size > 0 && <span style={{ background: '#e8f2f7', borderRadius: 0, padding: '1px 6px' }}>Staff: {[...filterStaff].map(n=>n.split(' ')[0]).join(', ')}</span>}
            {filterDept.size > 0 && <span style={{ background: '#e8f2f7', borderRadius: 0, padding: '1px 6px' }}>Dept: {[...filterDept].join(', ')}</span>}
            {filterStatus.size > 0 && <span style={{ background: '#e8f2f7', borderRadius: 0, padding: '1px 6px' }}>Status: {[...filterStatus].join(', ')}</span>}
            {filterService.size > 0 && <span style={{ background: '#e8f2f7', borderRadius: 0, padding: '1px 6px' }}>Service: {filterService.size} selected</span>}
            <span style={{ fontWeight: 600, color: '#374151' }}>{filteredJobs.length} jobs</span>
          </div>
        )}
      </div>

      {/* Jobs table */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Loading...</div>
      ) : viewMode === 'all' && !hasFilter ? (
        // All mode is lazy by design — wait for a filter before hitting the DB.
        <div className="card" style={{ textAlign: 'center', padding: 48, color: '#6b7280' }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>🔎</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#374151', marginBottom: 4 }}>
            Search across all history
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.5, maxWidth: 420, margin: '0 auto' }}>
            Type a stock number, or pick a Status / Dept filter, to load matching jobs from the database.
            Switch to <strong>Week</strong> or <strong>Year</strong> if you want to browse a period instead.
          </div>
        </div>
      ) : filteredJobs.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>
          No jobs found. {hasActiveFilter && <button onClick={clearAllFilters} style={{ color: '#4A7A91', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Clear filters</button>}
        </div>
      ) : (
        <>
        {/* KPI strip — the numbers the week is judged on, margin included.
            Everything here follows the active filters. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 0, flexWrap: 'wrap', background: '#f7f9fa', border: '1px solid #e3e7ea', padding: '11px 16px', marginBottom: 10 }}>
          <Kpi label="Jobs" value={totals.count} big
            sub={`${Math.round(jobUnits * 100) / 100} units`} />
          <KpiDiv />
          <Kpi label="Charged (inc GST)" value={`$${(totals.revenue * 1.1).toFixed(2)}`}
            sub={`ex $${totals.revenue.toFixed(2)} · GST $${totals.gst.toFixed(2)}`} />
          <KpiDiv />
          <Kpi label="Staff payout" value={`$${totals.payout.toFixed(2)}`} color="#c53030" />
          <KpiDiv />
          <Kpi label="Margin (ex GST)" value={`$${(totals.revenue - totals.payout).toFixed(2)}`} color="#15803d"
            badge={`${marginPct}%`} />

          {/* Needs attention — jobs whose status isn't approved. */}
          <button onClick={() => setNeedsAttention(v => !v)} style={{
            marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 7,
            height: 34, padding: '0 13px', borderRadius: 9, cursor: 'pointer',
            border: `1.5px solid ${needsAttention ? '#c53030' : '#f3c6c6'}`,
            background: needsAttention ? '#c53030' : '#fdeaea',
            color: needsAttention ? '#fff' : '#c53030', fontSize: 12, fontWeight: 800,
          }}>
            ⚠ Needs attention · {attentionCount}
          </button>
        </div>

        {/* Bulk action bar — only while rows are selected. Approving six
            pending jobs used to mean six separate row edits. */}
        {selectedIds.size > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', background: '#e8f2f7', borderBottom: '1px solid #b9d4e0', padding: '10px 16px', marginBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 800, color: '#1e3a4a' }}>
              {selectedIds.size} {selectedIds.size === 1 ? 'job' : 'jobs'} selected
            </span>
            <button onClick={() => setSelectedIds(new Set())} style={{ background: 'none', border: 'none', color: '#4A7A91', fontSize: 12, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>Clear</button>
            <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
              <button onClick={bulkApprove} disabled={bulkBusy} style={{ height: 32, padding: '0 14px', borderRadius: 8, border: 'none', background: '#059669', color: '#fff', fontSize: 12, fontWeight: 700, cursor: bulkBusy ? 'wait' : 'pointer' }}>
                {bulkBusy ? 'Working…' : 'Approve'}
              </button>
              <button onClick={bulkDelete} disabled={bulkBusy} style={{ height: 32, padding: '0 14px', borderRadius: 8, border: '1.5px solid #f3c6c6', background: '#fff', color: '#c53030', fontSize: 12, fontWeight: 700, cursor: bulkBusy ? 'wait' : 'pointer' }}>
                Delete
              </button>
            </span>
          </div>
        )}

        {/* Makes it obvious when a filter is hiding rows. */}
        <div style={{ fontSize: 11, fontWeight: 600, color: '#8a949c', margin: '0 2px 6px', ...NUMS }}>
          Showing {filteredJobs.length} of {jobs.length} {jobs.length === 1 ? 'job' : 'jobs'}
        </div>

        <div className="card" style={{ padding: 0, overflow: 'auto', maxHeight: 'calc(100vh - 260px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 10, boxShadow: '0 1px 0 #e5e7eb' }}>
                <th style={{ ...thStyle, width: 34 }}>
                  <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAll}
                    title="Select all visible" style={{ cursor: 'pointer' }} />
                </th>
                <th style={thStyle}>Job</th>
                <th style={thStyle}>Stock #</th>
                <th style={thStyle}>Model</th>
                <th style={thStyle}>Unit.</th>
                <th style={thStyle}>Service</th>
                <th style={thStyle}>Dept</th>
                <th style={thStyle}>Staff</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Charged</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Payout</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Margin</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredJobs.flatMap((job, jobIdx) => {
                const lines = parseBillingLines(job);
                const totalLinePay = lines.reduce((s, l) => s + (l.revenue_pay || 0), 0);
                const wInfo = job.week_number
                  ? { week: job.week_number }
                  : (() => { const wi = getISOWeek(job.date); return { week: wi.week }; })();
                // Parse comments safely
                let commentText = '';
                try { const c = JSON.parse(job.comments || '[]'); if (Array.isArray(c) && c.length) commentText = c.join(', '); } catch { commentText = job.comments || ''; }

                // When service or dept filters are active, only render matching
                // billing lines — otherwise a Suzuki car job whose only line is
                // a SERVICE-dept FIRST CLEAN would render that SERVICE line
                // inside a SUZUKI CARS view.
                let visibleLines = lines;
                if (filterService.size > 0) visibleLines = visibleLines.filter(bl => filterService.has(bl.service));
                if (filterDept.size > 0)    visibleLines = visibleLines.filter(bl => filterDept.has(bl.department));

                // Day group header — emitted when the date changes, so each
                // day reconciles on its own line instead of by hand-scrolling.
                const prev = filteredJobs[jobIdx - 1];
                const startsDay = visibleLines.length > 0 && (!prev || prev.date !== job.date);
                const dt = dayTotals[job.date || ''] || { jobs: 0, units: 0, charged: 0, payout: 0 };
                const dayHeader = startsDay ? [(
                  <tr key={`day-${job.date}`}>
                    <td colSpan={14} style={{ padding: 0 }}>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
                        background: '#f4f6f8', borderLeft: '3px solid #1e3a4a',
                        padding: '8px 12px', marginTop: 8,
                      }}>
                        <span style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: 1.1, ...NUMS }}>{dayHeading(job.date)}</span>
                        <span style={{ fontSize: 10.5, fontWeight: 600, color: '#8a949c', ...NUMS }}>
                          {dt.jobs} {dt.jobs === 1 ? 'job' : 'jobs'} · {Math.round(dt.units * 100) / 100} units
                        </span>
                        <span style={{ flex: 1 }} />
                        <span style={{ fontSize: 12.5, fontWeight: 800, ...NUMS }}>${(dt.charged * 1.1).toFixed(2)}</span>
                        <span style={{ fontSize: 12.5, fontWeight: 800, color: '#c53030', ...NUMS }}>${dt.payout.toFixed(2)}</span>
                        <span style={{ fontSize: 12.5, fontWeight: 800, color: '#15803d', ...NUMS }}>${(dt.charged - dt.payout).toFixed(2)}</span>
                      </div>
                    </td>
                  </tr>
                )] : [];

                return dayHeader.concat(visibleLines.map((bl, blIdx) => {
                  const isFirst = blIdx === 0;
                  // The left accent bar and the tint mean "this row needs you".
                  // Painting every row green made the one job that actually
                  // needs attention invisible, so only non-settled statuses get
                  // marked; approved/completed rows stay plain.
                  const flag = ATTENTION_ROW[job.status] || null;
                  // Plain white unless the row needs attention — the zebra
                  // striping competed with the exception tint.
                  const rowBg = flag ? flag.bg : '#ffffff';

                  // Inline edit helpers
                  const blKey    = (field) => `bl-${bl.lineId}-${field}`;
                  const jobKey   = (field) => `job-${job.id}-${field}`;
                  const isActive = (key)   => editingCell?.key === key;
                  const isSaving = (key)   => savingCell === key;

                  const cellStyle = (key) => ({
                    cursor: 'text',
                    outline: isActive(key) ? '2px solid #4A7A91' : 'none',
                    background: isActive(key) ? '#eff6ff' : undefined,
                    borderRadius: 0,
                  });

                  const inlineInput = (key, onSave) => (
                    <input
                      autoFocus
                      value={editingCell?.key === key ? editingCell.value : ''}
                      onChange={e => setEditingCell({ key, value: e.target.value })}
                      onBlur={() => onSave(editingCell?.value ?? '')}
                      onKeyDown={e => handleCellKeyDown(e, () => onSave(editingCell?.value ?? ''), () => setEditingCell(null))}
                      style={{ width: '100%', border: 'none', background: 'transparent', fontSize: 12, outline: 'none', padding: 0 }}
                    />
                  );

                  const inlineNumber = (key, onSave) => (
                    <input
                      autoFocus
                      type="number"
                      step="0.01"
                      value={editingCell?.key === key ? editingCell.value : ''}
                      onChange={e => setEditingCell({ key, value: e.target.value })}
                      onBlur={() => onSave(editingCell?.value ?? '')}
                      onKeyDown={e => handleCellKeyDown(e, () => onSave(editingCell?.value ?? ''), () => setEditingCell(null))}
                      style={{ width: 70, border: 'none', background: 'transparent', fontSize: 12, outline: 'none', padding: 0 }}
                    />
                  );

                  return (
                    <tr key={`${job.id}-${blIdx}`} className="jobs-row" style={{
                      background: rowBg,
                      boxShadow: flag ? `inset 3px 0 0 ${flag.bar}` : 'none',
                    }}>
                      {/* ── Every billing line shows full job info ── */}
                      {/* Selection — only on the job's first billing line, so
                          one tick selects the whole job. */}
                      <td style={{ padding: '4px 5px' }}>
                        {isFirst && (
                          <input type="checkbox" checked={selectedIds.has(job.id)}
                            onChange={() => toggleSelect(job.id)} style={{ cursor: 'pointer' }} />
                        )}
                      </td>

                      {/* Job id. The week badge only earns its place when the
                          view spans more than one week — in Week mode the
                          toolbar already says which week this is. The old NEW
                          badge is gone: 'New' is a status, and the STATUS chip
                          already answers it. */}
                      <td style={{ fontSize: 11, padding: '4px 5px', whiteSpace: 'nowrap', fontFamily: 'ui-monospace, Menlo, monospace', color: '#6b7280' }}>
                        {`#${job.id}`}
                        {viewMode !== 'week' && (
                          <span style={{ background: '#F5C518', color: '#1A1A1A', fontWeight: 700, padding: '0 3px', borderRadius: 3, fontSize: 9, marginLeft: 5 }}>W{wInfo.week}</span>
                        )}
                      </td>

                      {/* Stock # — inline editable */}
                      <td style={{ fontWeight: 600, padding: '4px 5px', fontSize: 12, ...cellStyle(jobKey('stock_number')) }}
                        onClick={() => !editingCell && startEdit(jobKey('stock_number'), job.stock_number || '')}>
                        {isActive(jobKey('stock_number'))
                          ? inlineInput(jobKey('stock_number'), v => commitJobField(job.id, 'stock_number', v))
                          : isSaving(jobKey('stock_number'))
                            ? <span style={{ color: '#9ca3af' }}>{job.stock_number}</span>
                            : <>{stockDisplay(job)}</>
                        }
                      </td>

                      {/* Model — inline editable */}
                      <td style={{ padding: '4px 5px', fontSize: 12, ...cellStyle(jobKey('model')) }}
                        onClick={() => !editingCell && startEdit(jobKey('model'), job.model || '')}>
                        {isActive(jobKey('model'))
                          ? inlineInput(jobKey('model'), v => commitJobField(job.id, 'model', v))
                          : isSaving(jobKey('model'))
                            ? <span style={{ color: '#9ca3af' }}>{titleCaseModel(job.model)}</span>
                            : (titleCaseModel(job.model) || "—")
                        }
                      </td>

                      {/* Unit. — job-level, same value on every line, editable from any line */}
                      <td style={{ padding: '4px 6px', fontSize: 12, textAlign: 'center', ...cellStyle(jobKey('units')) }}
                        onClick={() => !editingCell && startEdit(jobKey('units'), job.units ?? 1)}>
                        {isActive(jobKey('units'))
                          ? inlineNumber(jobKey('units'), v => commitJobField(job.id, 'units', parseFloat(v) || 1))
                          : isSaving(jobKey('units'))
                            ? <span style={{ color: '#9ca3af' }}>{job.units ?? 1}</span>
                            /* Almost every row is 1, so a plain unit is muted
                               and only a split job (< 1) catches the eye. */
                            : <span style={{ fontWeight: (job.units && job.units < 1) ? 700 : 400, color: (job.units && job.units < 1) ? '#c2410c' : '#8a949c', ...NUMS }}>{job.units ?? 1}</span>
                        }
                      </td>

                      {/* Service — inline editable (per billing line) */}
                      <td style={{ fontSize: 12, padding: '4px 5px', minWidth: 95, ...cellStyle(blKey('service')) }}
                        onClick={() => bl.lineId && !editingCell && startEdit(blKey('service'), bl.service)}>
                        {/* The comment lost its own column, so it's edited from
                            here: clicking the note (or the '+ note' stub) opens
                            the comment editor; clicking the service name still
                            edits the service. */}
                        {/* Only the first line renders the comment editor. The
                            comment is job-level, so rendering it on every
                            billing line mounted two autoFocus inputs — the
                            second stole focus, the first blurred and saved
                            itself shut, and editing looked broken. */}
                        {isFirst && isActive(jobKey('comments'))
                          ? inlineInput(jobKey('comments'), v => commitJobField(job.id, 'comments', JSON.stringify(v ? [v] : [])))
                          : bl.lineId && isActive(blKey('service'))
                            ? inlineInput(blKey('service'), v => commitBillingLine(bl.lineId, 'service', v))
                            : isSaving(blKey('service'))
                              ? <span style={{ color: '#9ca3af' }}>{bl.service}</span>
                              : (
                                <>
                                  {/* First Clean means the car is brand new —
                                      flag it so a new car is spottable. */}
                                  {bl.service === 'FIRST CLEAN' && (
                                    <span style={{
                                      background: '#dcfce7', color: '#15803d', fontSize: 8.5, fontWeight: 800,
                                      letterSpacing: 0.6, padding: '2px 5px', borderRadius: 4, marginRight: 5,
                                    }}>NEW</span>
                                  )}
                                  <span style={{ fontWeight: 600 }}>{bl.service}</span>
                                  {/* Only a real note — a comment that just repeats
                                      the service name told us nothing. */}
                                  {/* A note shows inline; with no note the '+'
                                      only appears on hover (CSS), so 40 empty
                                      rows don't carry '+ note' and wrap. */}
                                  {isFirst && (
                                    <span title="Click to edit the comment"
                                      className={realNote(commentText, bl.service) || isSaving(jobKey('comments')) ? '' : 'jobs-note-add'}
                                      onClick={e => { e.stopPropagation(); if (!editingCell) startEdit(jobKey('comments'), commentText); }}
                                      style={{ fontSize: 11, color: '#8a949c', cursor: 'pointer' }}>
                                      {isSaving(jobKey('comments'))
                                        ? ' · saving…'
                                        : realNote(commentText, bl.service)
                                          ? ` · ${realNote(commentText, bl.service)}`
                                          : ' +'}
                                    </span>
                                  )}
                                </>
                              )}
                      </td>

                      {/* Department — inline dropdown (per billing line) */}
                      <td style={{ padding: '4px 3px', minWidth: 85 }}
                        onClick={() => bl.lineId && !editingCell && !isActive(blKey('department')) && startEdit(blKey('department'), bl.department)}>
                        {bl.lineId && isActive(blKey('department')) ? (
                          <select
                            autoFocus
                            value={editingCell.value}
                            onChange={e => {
                              const v = e.target.value;
                              setEditingCell(prev => ({ ...prev, value: v }));
                              commitBillingLine(bl.lineId, 'department', v);
                            }}
                            onBlur={() => setEditingCell(null)}
                            style={{ fontSize: 11, border: '1px solid #4A7A91', borderRadius: 0, padding: '1px 4px' }}
                          >
                            {ALL_DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                          </select>
                        ) : isSaving(blKey('department')) ? (
                          <span style={{ color: '#9ca3af', fontSize: 11 }}>saving...</span>
                        ) : (
                          <span title="Click to change department" style={{ cursor: 'pointer' }}>
                            <DeptBadge dept={bl.department} />
                          </span>
                        )}
                      </td>

                      {/* Staff — shown on every line */}
                      <td style={{ fontSize: 12, padding: '4px 5px' }}>
                        {firstName(job.assigned_employees) || <span style={{ color: '#9ca3af' }}>—</span>}
                      </td>

                      {/* Charged — inc GST on top, ex GST underneath (the ex
                          figure stays the inline-editable one). */}
                      <td style={{ padding: '4px 5px', textAlign: 'right', ...cellStyle(blKey('revenue_full')) }}
                        onClick={() => bl.lineId && !editingCell && startEdit(blKey('revenue_full'), bl.revenue_full)}>
                        {bl.lineId && isActive(blKey('revenue_full'))
                          ? inlineNumber(blKey('revenue_full'), v => commitBillingLine(bl.lineId, 'revenue_full', parseFloat(v) || 0))
                          /* One figure per row — the ex-GST split lives in the
                             KPI strip and the totals, nobody reconciles it
                             line by line. */
                          : (
                            <span style={{ fontWeight: 700, ...NUMS, color: isSaving(blKey('revenue_full')) ? '#9ca3af' : '#1a1a1a' }}
                              title={`ex GST $${bl.revenue_full.toFixed(2)}`}>
                              ${(bl.revenue_full * 1.1).toFixed(2)}
                            </span>
                          )}
                      </td>

                      {/* Payout — per billing line, inline editable */}
                      <td style={{ color: '#c53030', fontWeight: 700, padding: '4px 5px', textAlign: 'right', ...NUMS, ...cellStyle(blKey('revenue_pay')) }}
                        onClick={() => !singleStaffId && bl.lineId && !editingCell && startEdit(blKey('revenue_pay'), bl.revenue_pay)}>
                        {singleStaffId
                          ? (() => {
                              const empPayout = getEmployeePayout(job, singleStaffId);
                              const lineEmpPayout = totalLinePay > 0
                                ? empPayout * (bl.revenue_pay / totalLinePay)
                                : empPayout / lines.length;
                              return `$${lineEmpPayout.toFixed(2)}`;
                            })()
                          : bl.lineId && isActive(blKey('revenue_pay'))
                            ? inlineNumber(blKey('revenue_pay'), v => commitBillingLine(bl.lineId, 'revenue_pay', parseFloat(v) || 0))
                            : isSaving(blKey('revenue_pay'))
                              ? <span style={{ color: '#9ca3af' }}>${bl.revenue_pay.toFixed(2)}</span>
                              : `$${bl.revenue_pay.toFixed(2)}`}
                      </td>

                      {/* Margin — ex GST charged minus payout. Red when the
                          line loses money; this is the number the business
                          runs on and it had no column before. */}
                      <td style={{ padding: '4px 5px', textAlign: 'right' }}>
                        {(() => {
                          const m = bl.revenue_full - bl.revenue_pay;
                          const pct = bl.revenue_full > 0 ? (m / bl.revenue_full) * 100 : 0;
                          const neg = m < 0;
                          return (
                            <>
                              <div style={{ fontWeight: 800, color: neg ? '#c53030' : '#15803d', ...NUMS }}>
                                {neg ? '−' : ''}${Math.abs(m).toFixed(2)}
                              </div>
                              <div style={{ fontSize: 10, fontWeight: 700, color: neg ? '#c53030' : '#8a949c', ...NUMS }}>
                                {neg ? '−' : ''}{Math.abs(Math.round(pct))}%
                              </div>
                            </>
                          );
                        })()}
                      </td>

                      {/* Status & Actions — shown on every line */}
                      <td style={{ padding: '4px 4px' }}>
                        <StatusChip status={job.status} />
                      </td>
                      {/* Row menu — one dots button instead of a bare trash
                          icon on every row. Approve only shows while the job
                          still needs it. */}
                      <td style={{ padding: '3px 4px', whiteSpace: 'nowrap', position: 'relative' }}>
                        {isFirst && (
                          <>
                            <button title="Actions" onClick={() => setOpenMenuId(id => id === job.id ? null : job.id)}
                              style={{ padding: '2px 6px', fontSize: 15, lineHeight: 1, border: 'none', background: 'none', color: '#c3ccd3', cursor: 'pointer' }}>⋯</button>
                            {openMenuId === job.id && (
                              <>
                                <div onClick={() => setOpenMenuId(null)} style={{ position: 'fixed', inset: 0, zIndex: 30 }} />
                                <div style={{
                                  position: 'absolute', right: 4, top: '100%', zIndex: 31, minWidth: 132,
                                  background: '#fff', border: '1px solid #e5e9ec', borderRadius: 10,
                                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: 4,
                                }}>
                                  {job.status !== 'approved' && (
                                    <button onClick={() => { setOpenMenuId(null); handleApprove(job.id); }} style={menuItem('#059669')}>Approve</button>
                                  )}
                                  <button onClick={() => { setOpenMenuId(null); job.is_office_cleaner ? setReassigning(job) : setEditingJob(job); }} style={menuItem('#374151')}>
                                    {job.is_office_cleaner ? 'Change who did it' : 'Edit'}
                                  </button>
                                  <button onClick={() => { setOpenMenuId(null); handleDelete(job.id); }} style={menuItem('#c53030')}>Delete</button>
                                </div>
                              </>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  );
                }));
              })}
            </tbody>
            <tfoot>
              {/* Sticky footer — always recomputed from the visible set, so a
                  filtered view never shows week totals. */}
              <tr style={{ position: 'sticky', bottom: 0, background: '#0a0a0a', color: '#fff' }}>
                <td colSpan={9} style={{ padding: '8px 10px', fontSize: 12, fontWeight: 800, ...NUMS }}>
                  {totals.count} {totals.count === 1 ? 'job' : 'jobs'}
                  <span style={{ color: '#8a949c', fontWeight: 600, marginLeft: 10 }}>
                    {hasActiveFilter ? 'Filtered subtotal' : 'Period total'}
                  </span>
                </td>
                <td style={{ padding: '6px 5px', textAlign: 'right' }}>
                  <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 0.8, color: '#7a7a7a' }}>CHARGED INC GST</div>
                  <div style={{ fontSize: 13, fontWeight: 800, ...NUMS }}>${(totals.revenue * 1.1).toFixed(2)}</div>
                </td>
                <td style={{ padding: '6px 5px', textAlign: 'right' }}>
                  <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: 0.8, color: '#7a7a7a' }}>PAYOUT</div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#ff8a8a', ...NUMS }}>${totals.payout.toFixed(2)}</div>
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                  <span style={{ display: 'inline-block', background: '#FFD000', color: '#0a0a0a', borderRadius: 8, padding: '5px 12px', ...NUMS }}>
                    <span style={{ fontSize: 9.5, fontWeight: 700, marginRight: 6 }}>
                      MARGIN {marginPct}%
                    </span>
                    <span style={{ fontSize: 15, fontWeight: 800 }}>${(totals.revenue - totals.payout).toFixed(2)}</span>
                  </span>
                </td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          </table>

        </div>
        </>
      )}

      {reassigning && (
        <ReassignCleanModal
          row={reassigning}
          contractors={contractors}
          onClose={() => setReassigning(null)}
          onSaved={() => { setReassigning(null); loadData(); }}
        />
      )}

      {editingJob && (
        <EditJobModal
          job={editingJob}
          contractors={contractors}
          onSave={() => { setEditingJob(null); loadData(); }}
          onClose={() => setEditingJob(null)}
          onDelete={id => { setEditingJob(null); handleDelete(id); }}
        />
      )}


      </>
      )}

    </div>
  );
}

// ── Change who did an office clean ──────────────────────────────────────────
// Ana was off for three days and Luana covered, and there was no way to say so
// anywhere in the app: these rows come from clock_events, so the job editor
// hit "invalid input syntax for type integer: oc-1700-0" on save. Nothing else
// about the record needs editing here — Attendance already handles times and
// services — only the name on it.
function ReassignCleanModal({ row, contractors, onClose, onSaved }) {
  const eventId = String(row.id || '').split('-')[1];
  const current = row.submitted_by;
  const [userId, setUserId] = useState(current);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  // Whoever can hold an attendance record. Includes inactive staff: a day
  // covered months ago may belong to someone who has since left.
  const options = (contractors || []).filter(c => ['office_cleaner', 'hourly'].includes(c.type));

  async function save() {
    if (!eventId || Number(userId) === Number(current)) { onClose(); return; }
    setSaving(true); setError('');
    try {
      await api.updateAttendance(eventId, { user_id: Number(userId) });
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally { setSaving(false); }
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', width: '100%', maxWidth: 420, borderRadius: 8, padding: 22 }}>
        <div style={{ fontSize: 17, fontWeight: 800, color: '#1a1a2e' }}>Change who did it</div>
        <div style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 18px' }}>
          {row.job_description} · {row.date}
        </div>

        <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: '#9ca3af' }}>CONTRACTOR</label>
        <select value={userId} onChange={e => setUserId(e.target.value)}
          style={{ width: '100%', height: 42, padding: '0 10px', border: '1.5px solid #dfe3e7', borderRadius: 4, fontSize: 14, marginTop: 6, boxSizing: 'border-box' }}>
          {options.map(c => (
            <option key={c.id} value={c.id}>{c.name}{c.is_active ? '' : ' (inactive)'}</option>
          ))}
        </select>

        <p style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5, margin: '12px 0 0' }}>
          Moves the whole day — the hours, the services and the pay go with it.
        </p>

        {error && (
          <div style={{ marginTop: 12, background: '#fee2e2', color: '#b91c1c', borderRadius: 6, padding: '9px 11px', fontSize: 13, fontWeight: 600 }}>{error}</div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
          <button onClick={onClose} style={{ padding: '9px 18px', border: '1.5px solid #dfe3e7', background: '#fff', borderRadius: 4, fontSize: 13, fontWeight: 700, color: '#374151', cursor: 'pointer' }}>Cancel</button>
          <button onClick={save} disabled={saving} style={{ padding: '9px 20px', border: 'none', borderRadius: 4, background: saving ? '#9ca3af' : '#4A7A91', color: '#fff', fontSize: 13, fontWeight: 800, cursor: saving ? 'wait' : 'pointer' }}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
